import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ProductAdAccountConnections,
  ProductAdCampaign,
  ProductAdCampaignRef,
  productAdAccountsService,
} from '../../../services/api/knowledge/productAdAccountsService';

interface ProductAdAccountsTabProps {
  productId?: number;
}

function errorMessage(error: unknown, fallback: string): string {
  const typed = error as { response?: { data?: { message?: string } } };
  return typed.response?.data?.message || fallback;
}

const sameCampaign = (a: ProductAdCampaignRef, b: ProductAdCampaignRef) =>
  a.ad_account_id === b.ad_account_id && a.campaign_id === b.campaign_id;

const toRefs = (campaigns: ProductAdCampaignRef[] = []): ProductAdCampaignRef[] =>
  campaigns.map(({ ad_account_id, campaign_id }) => ({ ad_account_id, campaign_id }));

/** アカウントごとの選択肢。紐付け済みでも配信実績から消えたキャンペーンは外せるように残す。 */
const campaignsOf = (connections: ProductAdAccountConnections | null, accountId: number): ProductAdCampaign[] => {
  const all = [...(connections?.available_campaigns || []), ...(connections?.campaigns || [])]
    .filter((campaign) => campaign.ad_account_id === accountId);
  return Array.from(new Map(all.map((campaign) => [campaign.campaign_id, campaign])).values());
};

const ProductAdAccountsForm: React.FC<{ productId: number }> = ({ productId }) => {
  const [connections, setConnections] = useState<ProductAdAccountConnections | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<ProductAdCampaignRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const requestVersion = useRef(0);

  const apply = (data: ProductAdAccountConnections) => {
    setConnections(data);
    setSelected(data.accounts.map((account) => account.id));
    setSelectedCampaigns(toRefs(data.campaigns));
  };

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await productAdAccountsService.get(productId);
      if (requestVersion.current !== version) return;
      apply(data);
    } catch (err) {
      if (requestVersion.current === version) {
        setError(errorMessage(err, '広告アカウントの接続状態を取得できませんでした。'));
      }
    } finally {
      if (requestVersion.current === version) setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    load();
    return () => { requestVersion.current += 1; };
  }, [load]);

  const save = async () => {
    const version = ++requestVersion.current;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // 外したアカウントのキャンペーンは送らない（API は ad_account_ids に無いものを 422 にする）。
      const campaigns = selectedCampaigns.filter((campaign) => selected.includes(campaign.ad_account_id));
      const data = await productAdAccountsService.save(productId, selected, campaigns);
      if (requestVersion.current !== version) return;
      apply(data);
      setSuccess('広告アカウントの紐付けを保存しました。');
    } catch (err) {
      if (requestVersion.current === version) {
        setError(errorMessage(err, '広告アカウントの紐付けを保存できませんでした。'));
      }
    } finally {
      if (requestVersion.current === version) setSaving(false);
    }
  };

  const availableIds = new Set(connections?.available_accounts.map((account) => account.id));
  const accounts = Array.from(new Map([
    ...(connections?.accounts || []), ...(connections?.available_accounts || []),
  ].map((account) => [account.id, account])).values());
  const savedIds = connections?.accounts.map((account) => account.id) || [];
  const savedCampaigns = toRefs(connections?.campaigns);
  const dirty = selected.length !== savedIds.length || selected.some((id) => !savedIds.includes(id))
    || selectedCampaigns.length !== savedCampaigns.length
    || selectedCampaigns.some((campaign) => !savedCampaigns.some((saved) => sameCampaign(saved, campaign)));
  const busy = loading || saving;

  return (
    <section aria-labelledby="product-ad-accounts-heading" className="space-y-5">
      <div className="pb-4 border-b border-warm-border">
        <h2 id="product-ad-accounts-heading" className="text-xl font-semibold text-dark">広告連携</h2>
      </div>

      <div className="rounded-tasky-md border border-warm-border bg-warm-surface p-4 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/settings/media-connect" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
            SNS連携でアカウントを接続（別タブ）
          </Link>
          <button type="button" onClick={load} disabled={busy} className="rounded-tasky-sm border border-warm-border bg-white px-3 py-1.5 text-xs font-medium disabled:opacity-50">
            接続状態を更新
          </button>
        </div>
      </div>

      {error && <p role="alert" className="rounded-tasky-sm bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {success && <p role="status" className="rounded-tasky-sm bg-green-50 p-3 text-sm text-green-700">{success}</p>}
      {loading && <p className="text-sm text-gray-500">広告アカウントを読み込み中...</p>}

      {connections && !loading && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            {(['google', 'meta'] as const).map((platform) => {
              const providerAccounts = accounts.filter((account) => account.platform === platform);
              return (
                <div key={platform} className="rounded-tasky-md border border-warm-border bg-white p-4">
                  <h3 className="mb-3 text-base font-semibold text-dark">{platform === 'google' ? 'Google Ads' : 'Meta Ads'}</h3>
                  {providerAccounts.length === 0 && <p className="text-sm text-gray-500">接続済みのアカウントはありません</p>}
                  <div className="space-y-3">
                    {providerAccounts.map((account) => {
                      const available = availableIds.has(account.id);
                      const checked = selected.includes(account.id);
                      const campaigns = checked ? campaignsOf(connections, account.id) : [];
                      const accountName = account.account_name || account.platform_account_id;
                      return (
                        <div key={account.id} className="rounded-tasky-sm border border-warm-border p-3">
                          <label className="flex cursor-pointer items-start gap-3">
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4 accent-primary"
                              checked={checked}
                              disabled={busy || (!available && !checked)}
                              onChange={(event) => {
                                setSuccess('');
                                setSelected((current) => event.target.checked ? [...current, account.id] : current.filter((id) => id !== account.id));
                                if (!event.target.checked) {
                                  setSelectedCampaigns((current) => current.filter((campaign) => campaign.ad_account_id !== account.id));
                                }
                              }}
                            />
                            <span className="min-w-0 text-sm">
                              <span className="block break-words font-medium text-dark">{accountName}</span>
                              <span className="block break-all text-xs text-gray-500">{account.platform_account_id}</span>
                              <span className={`mt-1 block text-xs ${available ? 'text-green-700' : 'text-amber-700'}`}>
                                {available ? (savedIds.includes(account.id) ? 'この商品に紐付け済み' : '接続済み') : '再接続が必要です'}
                              </span>
                            </span>
                          </label>
                          {campaigns.length > 0 && (
                            <div role="group" aria-label={`${accountName} のキャンペーン`} className="mt-3 space-y-2 border-t border-warm-border pl-7 pt-3">
                              {campaigns.map((campaign) => {
                                const ref = { ad_account_id: account.id, campaign_id: campaign.campaign_id };
                                return (
                                  <label key={campaign.campaign_id} className="flex cursor-pointer items-center gap-2 text-sm text-dark">
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 accent-primary"
                                      checked={selectedCampaigns.some((selectedCampaign) => sameCampaign(selectedCampaign, ref))}
                                      disabled={busy}
                                      onChange={(event) => {
                                        setSuccess('');
                                        setSelectedCampaigns((current) => event.target.checked
                                          ? [...current, ref]
                                          : current.filter((selectedCampaign) => !sameCampaign(selectedCampaign, ref)));
                                      }}
                                    />
                                    <span className="min-w-0 break-words">{campaign.campaign_name || campaign.campaign_id}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={save} disabled={busy || !dirty} className="rounded-tasky-sm bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50">
              {saving ? '保存中...' : '紐付けを保存'}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

const ProductAdAccountsTab: React.FC<ProductAdAccountsTabProps> = ({ productId }) => (
  productId
    ? <ProductAdAccountsForm key={productId} productId={productId} />
    : <p className="text-sm text-gray-500">広告を紐付ける商品を選択してください。</p>
);

export default ProductAdAccountsTab;
